import { Sparkles, QrCode, Smartphone, Car } from "lucide-react";

/**
 * Vermittler activity statistics — KPI tiles + per-category breakdown bars.
 * Extracted from VermittlerDashboardPage.
 */
const ACTIVITY_LABELS = {
    qr_smilie: { label: "QR-Code Smilie", icon: QrCode, color: "text-amber-700 bg-amber-100" },
    nfc: { label: "NFC-Aktion", icon: Smartphone, color: "text-blue-700 bg-blue-100" },
    referral_vehicle: { label: "Fahrzeug-Vermittlung", icon: Car, color: "text-green-700 bg-green-100" },
    other: { label: "Sonstige Aktion", icon: Sparkles, color: "text-purple-700 bg-purple-100" },
};
const CATS = ["qr_smilie", "nfc", "referral_vehicle", "other"];

export default function ActivityStats({ activities, totals }) {
    const byCat = CATS.reduce((acc, c) => {
        const filtered = activities.filter((a) => a.category === c);
        acc[c] = {
            count: filtered.length,
            points: filtered.reduce((s, a) => s + (Number(a.points) || 0), 0),
        };
        return acc;
    }, {});
    const totalCount = activities.length;
    const totalPoints = totals.awarded || 0;
    const avgPoints = totalCount > 0 ? Math.round(totalPoints / totalCount) : 0;
    const topCat = CATS.reduce((best, c) => {
        if (byCat[c].points > (byCat[best]?.points || 0)) return c;
        return best;
    }, CATS[0]);
    const conversionPct = totalCount > 0
        ? Math.round((byCat.referral_vehicle.count / totalCount) * 100)
        : 0;

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="vermittler-stats">
            <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-5 h-5 text-[#FFCC00]" />
                <h2 className="font-display font-bold text-lg">Aktivitäts-Statistik</h2>
            </div>
            <p className="text-xs text-gray-600 mb-4">
                Übersicht über Ihre Vermittlungs-Performance — Punkte-Verteilung pro Kategorie und Konversionsrate.
            </p>

            {totalCount === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500 border border-dashed border-gray-200 rounded-sm" data-testid="stats-empty">
                    Noch keine Aktivitäten erfasst. Starten Sie eine QR-Code- oder NFC-Aktion!
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="stats-kpis">
                        <div className="border border-gray-200 rounded-sm p-3" data-testid="stat-total-activities">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Aktivitäten</div>
                            <div className="text-2xl font-black font-mono-num mt-1">{totalCount}</div>
                        </div>
                        <div className="border border-gray-200 rounded-sm p-3" data-testid="stat-avg-points">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Ø Punkte / Aktion</div>
                            <div className="text-2xl font-black font-mono-num mt-1">{avgPoints}</div>
                        </div>
                        <div className="border border-gray-200 rounded-sm p-3" data-testid="stat-conversion">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Fahrzeug-Quote</div>
                            <div className="text-2xl font-black font-mono-num mt-1 text-[#16A34A]">{conversionPct}%</div>
                            <div className="text-[10px] text-gray-500">Anteil echter Vermittlungen</div>
                        </div>
                        <div className="border border-gray-200 rounded-sm p-3 bg-amber-50 border-amber-300" data-testid="stat-top-cat">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Top-Kategorie</div>
                            <div className="text-sm font-black mt-1 text-amber-900">
                                {ACTIVITY_LABELS[topCat]?.label || "—"}
                            </div>
                            <div className="text-[10px] text-amber-700 font-mono-num">{byCat[topCat]?.points || 0} Punkte</div>
                        </div>
                    </div>

                    <div className="space-y-3" data-testid="stats-breakdown">
                        {CATS.map((c) => {
                            const data = byCat[c];
                            const meta = ACTIVITY_LABELS[c];
                            const Icon = meta.icon;
                            const pctOfPoints = totalPoints > 0 ? (data.points / totalPoints) * 100 : 0;
                            return (
                                <div key={c} data-testid={`stat-cat-${c}`}>
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <div className="flex items-center gap-2">
                                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-sm ${meta.color}`}>
                                                <Icon className="w-3.5 h-3.5" />
                                            </span>
                                            <span className="font-bold text-[#111827]">{meta.label}</span>
                                            <span className="text-gray-500">· {data.count} {data.count === 1 ? "Aktion" : "Aktionen"}</span>
                                        </div>
                                        <span className="font-mono-num font-bold">{data.points} P · {pctOfPoints.toFixed(0)}%</span>
                                    </div>
                                    <div className="h-2 bg-gray-100 rounded-sm overflow-hidden">
                                        <div
                                            className="h-full bg-[#FFCC00] transition-all"
                                            style={{ width: `${pctOfPoints}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
